/**
 * Names behind the anonymous ids in Tauri's
 * `[TAURI] Couldn't find callback id <n>` warning.
 *
 * The id is a `crypto.getRandomValues(new Uint32Array(1))[0]` and carries no
 * reference to the event or command it was minted for, so the warning on its
 * own cannot say what was orphaned. `window.__TAURI_INTERNALS__.runCallback`
 * is defined non-writable, so it cannot be patched to add one either. Whoever
 * mints the id is the only party that knows the name — so that is where it is
 * recorded, here.
 *
 * Write side: `managed-listen.ts`. Read side: the console interceptor, wired
 * at bootstrap in `src/utils/runtime-wiring.ts`. This module deliberately
 * imports nothing from `@tauri-apps/api`, so the bootstrap wiring costs a
 * plain `Map` and no Tauri module evaluation.
 */

export type TauriCallbackKind = 'listen' | 'invoke';

export type TauriCallbackLabel = {
  kind: TauriCallbackKind;
  /** Event name for a `listen` handler, command name for an `invoke` callback. */
  name: string;
};

const labels = new Map<number, TauriCallbackLabel>();

export function recordCallbackLabel(id: number, label: TauriCallbackLabel): void {
  labels.set(id, label);
}

/**
 * Drop a label once its id can no longer be delivered to — the callback is
 * unregistered AND the emitter that fed it is gone.
 *
 * That is the SUCCESS path, and it is the only path this is called from: an id
 * whose teardown did NOT complete keeps its label on purpose, because that is
 * exactly the id a warning would name. So the map is bounded by live
 * subscriptions PLUS every id whose teardown gave up — the ids that still warn
 * — not by live subscriptions alone.
 *
 * The second term is not hypothetical in the environment this labelling exists
 * for. In a webview whose `plugin:event|unlisten` is permanently denied, the
 * reference-counted `AUDIO_LEVELS` subscription claims and releases on every
 * consumer mount, so an all-day session adds one permanent entry per cycle.
 * Each is a number key and a two-string object, so this is bookkeeping growth
 * rather than a crash risk — and it is the deliberate price of the warning
 * being able to say `AUDIO_LEVELS` instead of a bare random u32. Stated here
 * rather than capped so the next reader does not take the bound for a
 * guarantee it never was.
 */
export function forgetCallbackLabel(id: number): void {
  labels.delete(id);
}

/**
 * The exact shape the warning carries: `transformCallback` mints ids from
 * `crypto.getRandomValues(new Uint32Array(1))[0]`, so the digits the
 * interceptor's `Couldn't find callback id (\d+)` captures are always plain
 * decimal. Anchored and digits-only on purpose — `Number()` would also accept
 * `''` (0), `'0x10'` (16), `'1e3'` (1000) and `' 7 '` (7), each of which is a
 * lookup this map has no business answering.
 */
const DECIMAL_ID = /^\d+$/;

/**
 * Look up a label from the id as it appears in the warning text (a decimal
 * u32 string). Returns undefined for anything not minted through this repo's
 * wrapper, which is most ids today — the migration is incremental.
 */
export function describeCallbackId(id: string): TauriCallbackLabel | undefined {
  if (!DECIMAL_ID.test(id)) return undefined;
  return labels.get(Number(id));
}

export function __resetCallbackLabelsForTests(): void {
  labels.clear();
}
